import Cursor, { CursorTypes } from 'editor/components/Cursor';
import Events from 'editor/components/Events';
import MoveMarkupInteraction from 'editor/components/interactions/MoveMarkupInteraction';
import {
  MapboxEventArgs,
  MapboxToolEvents,
} from 'editor/components/map/MapboxEvents/mapboxEventHandlers';
import Markup from 'editor/components/Markup';
import MarkupKit from 'editor/components/MarkupKit';
import PageID from 'editor/components/PageID';
import PlacePunch from 'editor/components/PlacePunch';
import { getSheetLayerHit, getSheetLayerHits } from 'editor/utils/map';
import MapSheetEvents from '../MapSheetEvents';

const startEvent = (
  args: MapboxEventArgs<mapboxgl.MapEventType['mousedown' | 'touchstart']>
) => {
  const { evt, world } = args;

  const sheetLayerHits = getSheetLayerHits(
    world,
    evt.lngLat.lng,
    evt.lngLat.lat
  );

  const layerHit = sheetLayerHits.find((layer) => layer.hit !== null);
  if (!layerHit) return;

  const { hit } = layerHit;
  if (!hit) return;

  const markupKit = world.getComponent(MarkupKit);
  if (!markupKit) return;

  world
    .grabAll(Markup)
    .filter(({ component }) => component.isNewPunch === true)
    .forEach(({ entity: newPunchMarkupEntity, component: markupComp }) => {
      const l = hit.clone();
      const point = new markupKit.MKPoint(l.x, l.y);
      const isHit = markupComp.mkMarkup?.hit(point);

      if (isHit) {
        const [left, bottom] = markupComp.mkMarkup?.getRect();

        newPunchMarkupEntity.add(
          new MoveMarkupInteraction({
            originX: hit.x,
            originY: hit.y,
            currentX: hit.x,
            currentY: hit.y,
            deltaX: hit.x - left,
            deltaY: hit.y - bottom,
          })
        );
      }
    });
};

const endEvent = (
  args: MapboxEventArgs<mapboxgl.MapEventType['mouseup' | 'touchend']>
) => {
  const { world, evt } = args;
  // Set all markups isNewPunch to false
  world.grabAll(Markup).forEach(({ component }) => {
    if (component.isNewPunch === true) {
      component.isNewPunch = false;
    }
  });
  const sheetLayerHits = getSheetLayerHits(
    world,
    evt.lngLat.lng,
    evt.lngLat.lat
  );

  const layerHit = sheetLayerHits.find((layer) => layer.hit !== null);
  if (!layerHit) return;

  const { entity: sheetLayerEntity, hit } = layerHit;
  if (!hit) return;

  const events = sheetLayerEntity.get<Events<MapSheetEvents>>(Events);
  if (!events) return;

  world.locateAll([Markup, MoveMarkupInteraction]).forEach((entity) => {
    const markupComp = entity.get(Markup);
    const { markup, mkMarkup } = markupComp;

    if (mkMarkup) {
      mkMarkup.setModDate(new Date().toISOString());
      const jsonBlob = JSON.stringify(mkMarkup.getAsObject());
      const nextMarkup = markup.set('jsonBlob', jsonBlob);

      events.events.emit('markup:move', { markup: nextMarkup });
    }

    entity.remove(MoveMarkupInteraction);
  });
};

const placePunchEvents: MapboxToolEvents = {
  mousedown: (args: MapboxEventArgs<mapboxgl.MapEventType['mousedown']>) => {
    startEvent(args);
  },

  mousemove: (args: MapboxEventArgs<mapboxgl.MapEventType['mousemove']>) => {
    const { evt, world } = args;

    // am I over a markup?????
    const sheetLayerHits = getSheetLayerHits(
      world,
      evt.lngLat.lng,
      evt.lngLat.lat
    );

    const layerHit = sheetLayerHits.find((layer) => layer.hit !== null);
    if (!layerHit) return;

    const { hit } = layerHit;

    world.grabAll(Cursor).forEach(({ component: cursorComp }) => {
      if (hit) {
        cursorComp.disabled = false;
        cursorComp.cursor = CursorTypes.Pointer;
      } else {
        cursorComp.disabled = true;
      }
    });

    const markupKit = world.getComponent(MarkupKit);
    if (!markupKit) return;

    const newPunchMarkupEntity = world.grabBy(
      Markup,
      (comp) => comp.isNewPunch === true
    )?.entity;

    if (newPunchMarkupEntity && hit) {
      const markupComp = newPunchMarkupEntity.get(Markup);

      const l = hit.clone();
      const point = new markupKit.MKPoint(l.x, l.y);
      const isHit = markupComp.mkMarkup?.hit(point);

      world.grabAll(Cursor).forEach(({ component }) => {
        if (isHit) {
          component.disabled = false;
          component.cursor = CursorTypes.Move;
        } else {
          component.disabled = true;
        }
      });

      // Update move markup interaction if needed
      world.grabAll(MoveMarkupInteraction).forEach(({ component }) => {
        component.currentX = hit.x;
        component.currentY = hit.y;
      });
    }
  },

  mouseup: (args: MapboxEventArgs<mapboxgl.MapEventType['mouseup']>) => {
    endEvent(args);
  },

  touchstart: (args: MapboxEventArgs<mapboxgl.MapEventType['touchstart']>) => {
    startEvent(args);
  },

  touchmove: (args: MapboxEventArgs<mapboxgl.MapEventType['touchmove']>) => {
    const { evt, world } = args;

    // am I over a markup?????
    const sheetLayerHits = getSheetLayerHits(
      world,
      evt.lngLat.lng,
      evt.lngLat.lat
    );

    const layerHit = sheetLayerHits.find((layer) => layer.hit !== null);
    if (!layerHit) return;

    const { hit } = layerHit;
    const markupKit = world.getComponent(MarkupKit);
    if (!markupKit) return;

    const newPunchMarkupEntity = world.grabBy(
      Markup,
      (comp) => comp.isNewPunch === true
    )?.entity;

    if (newPunchMarkupEntity && hit) {
      // Update move markup interaction if needed
      world.grabAll(MoveMarkupInteraction).forEach(({ component }) => {
        component.currentX = hit.x;
        component.currentY = hit.y;
      });
    }
  },

  touchend: (args: MapboxEventArgs<mapboxgl.MapEventType['touchend']>) => {
    endEvent(args);
  },

  click: (args: MapboxEventArgs<mapboxgl.MapEventType['click']>) => {
    const { evt, world } = args;

    const newPunchMarkupEntity = world.grabBy(
      Markup,
      (comp) => comp.isNewPunch === true
    )?.entity;

    const placePunchEntity = world.locate(PlacePunch);

    if (placePunchEntity) {
      const placePunchComponent = placePunchEntity.get(PlacePunch);
      const sheetLayerEntity = world.getTagged(
        `sheetLayer-${placePunchComponent.pageid}`
      );

      if (!sheetLayerEntity) return;

      const { hit } = getSheetLayerHit(
        sheetLayerEntity,
        evt.lngLat.lng,
        evt.lngLat.lat
      );

      if (!hit) {
        sheetLayerEntity
          .get<Events<MapSheetEvents>>(Events)
          ?.events.emit('place-punch:cancel');

        return;
      }

      const markupKit = world.getComponent(MarkupKit);
      if (!markupKit) return;

      const events = sheetLayerEntity.get<Events<MapSheetEvents>>(Events);
      if (!events) return;

      if (newPunchMarkupEntity) {
        const markupComp = newPunchMarkupEntity.get(Markup);

        const l = hit.clone();
        const point = new markupKit.MKPoint(l.x, l.y);
        const isHit = markupComp.mkMarkup?.hit(point);

        if (!isHit) {
          events.events.emit('place-punch:cancel');
        }
        return;
      }

      const pageIdComp = sheetLayerEntity.get(PageID);

      const markup = markupKit.MKMarkup.createCircle();
      const creationDate = new Date().toISOString();

      const radius = 10;
      markup.setCreationDate(creationDate);
      markup.setModDate(creationDate);
      markup.setWidth(5);
      markup.setFillColor(0.8901961, 0.16078432, 0.25882354);
      markup.setStrokeColor(0.8901961, 0.16078432, 0.25882354);
      markup.setRectDiff(0.5, 0.5, 0.5, 0.5);
      markup.setRect(
        hit.x - radius,
        hit.y - radius,
        hit.x + radius,
        hit.y + radius
      );

      events.events.emit('punch:create', {
        markup,
        pageid: pageIdComp.pageid,
      });

      const markupComp = new Markup();
      markupComp.mkMarkup = markup;
      markupComp.isNewPunch = true;
      markupComp.isPunch = false;
    }
  },
};

export default placePunchEvents;
